/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	EntityManagerDirective
} from '@admin/directives/entity-manager.directive';
import {
	Component,
} from '@angular/core';
import {
	UntypedFormControl
} from '@angular/forms';
import {
	ActivatedRoute,
	Params
} from '@angular/router';
import {
	EntityTypeApiService
} from '@api/services/entities/entity-type.api.service';
import {
	WorkflowActionDefinitionsApiService
} from '@api/services/workflow/workflow-action-definitions.api.service';
import {
	WorkflowActionRequisitesApiService
} from '@api/services/workflow/workflow-action-requisites.api.service';
import {
	WorkflowFailureActionsApiService
} from '@api/services/workflow/workflow-failure-actions.api.service';
import {
	WorkflowRequisiteTypesApiService
} from '@api/services/workflow/workflow-requisite-types.api.service';
import {
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	CommonTableComponent
} from '@shared/components/common-table/common-table.component';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	ObjectHelper
} from '@shared/helpers/object.helper';
import {
	TableHelper
} from '@shared/helpers/table.helper';
import {
	Activity
} from '@shared/implementations/application-data/activity';
import {
	ICommonTableColumn
} from '@shared/interfaces/application-objects/common-table-column.interface';
import {
	ICommonTable
} from '@shared/interfaces/application-objects/common-table.interface';
import {
	IDropdownOption
} from '@shared/interfaces/application-objects/dropdown-option.interface';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IObjectSearch
} from '@shared/interfaces/application-objects/object-search.interface';
import {
	IWorkflowActionDefinitions
} from '@shared/interfaces/workflow/workflow-action-definitions.interface';
import {
	IWorkflowActionRequisites
} from '@shared/interfaces/workflow/workflow-action-requisites.interface';
import {
	IWorkflowFailureActions
} from '@shared/interfaces/workflow/workflow-failure-actions.interface';
import {
	IWorkflowRequisiteTypes
} from '@shared/interfaces/workflow/workflow-requisite-types.interface';
import {
	ActivityService
} from '@shared/services/activity.service';
import {
	ResolverService
} from '@shared/services/resolver.service';
import {
	SiteLayoutService
} from '@shared/services/site-layout.service';

/* eslint-enable max-len */

@Component({
	selector: 'app-entity-action-definition',
	templateUrl: './entity-action-definition.component.html',
	styleUrls: [
		'./entity-action-definition.component.scss'
	]
})

/**
 * A component representing an instance of the entity action definitions
 * component.
 *
 * @export
 * @class EntityActionDefinitionComponent
 * @extends {EntityManagerDirective}
 */
export class EntityActionDefinitionComponent
	extends EntityManagerDirective
{
	/**
	 * Creates an instance of an EntityActionDefinitionComponent.
	 *
	 * @param {EntityTypeApiService} entityTypeApiService
	 * The api service used to get the entity type data.
	 * @param {WorkflowActionDefinitionsApiService}
	 * workflowActionDefinitionsApiService
	 * The api service used to get workflow action definition data.
	 * @param {WorkflowFailureActionsApiService}
	 * 	workflowFailureActionsApiService
	 * The api service used to get workflow failure action data.
	 * @param {WorkflowRequisiteTypesApiService}
	 * 	workflowRequisiteTypesApiService
	 * The api service used to get workflow requisite types data.
	 * @param {WorkflowActionRequisitesApiService}
	 * workflowActionRequisitesApiService
	 * The api service used to get workflow action requisite data.
	 * @param {SiteLayoutService} siteLayoutService
	 * The service used to get the site layout data.
	 * @param {ActivatedRoute} route
	 * The activated route that opened this component.
	 * @param {ActivityService} activityService
	 * The activity service used to handle data interactions and client
	 * messaging.
	 * @param {ResolverService} resolver
	 * The resolver service used for context methods in this component.
	 * @memberof EntityActionDefinitionComponent
	 */
	public constructor(
		public entityTypeApiService: EntityTypeApiService,
		public workflowActionDefinitionsApiService:
			WorkflowActionDefinitionsApiService,
		public workflowFailureActionsApiService:
			WorkflowFailureActionsApiService,
		public workflowRequisiteTypesApiService:
			WorkflowRequisiteTypesApiService,
		public workflowActionRequisitesApiService:
			WorkflowActionRequisitesApiService,
		public siteLayoutService: SiteLayoutService,
		public route: ActivatedRoute,
		public activityService: ActivityService,
		public resolver: ResolverService)
	{
		super(
			route,
			activityService,
			resolver);
	}

	/**
	 * Gets or sets the workflow action definition data.
	 *
	 * @type {IWorkflowActionDefinitions}
	 * @memberof EntityActionDefinitionComponent
	 */
	public workflowActionDefinition: IWorkflowActionDefinitions;

	/**
	 * Gets or sets the workflow action id.
	 *
	 * @type {number}
	 * @memberof EntityActionDefinitionComponent
	 */
	public actionId: number;

	/**
	 * Gets or sets the loading state for the post requisite table definitions.
	 *
	 * @type {boolean}
	 * @memberof EntityActionDefinitionComponent
	 */
	public loadingPostRequisiteTableDefinitions: boolean = true;

	/**
	 * Gets or sets the loading state for the pre requisite table definitions.
	 *
	 * @type {boolean}
	 * @memberof EntityActionDefinitionComponent
	 */
	public loadingPreRequisiteTableDefinitions: boolean = true;

	/**
	 * Gets or sets the workflow requisite types.
	 *
	 * @type {IWorkflowRequisiteTypes[]}
	 * @memberof EntityActionDefinitionComponent
	 */
	public requisiteTypes: IWorkflowRequisiteTypes[];

	/**
	 * Gets or sets the workflow requisite options.
	 *
	 * @type {IDropdownOption[]}
	 * @memberof EntityActionDefinitionComponent
	 */
	public requisiteOptions: IDropdownOption[];

	/**
	 * Gets or sets the PreRequisite table definitions.
	 *
	 * @type {ICommonTable}
	 * @memberof EntityActionDefinitionComponent
	 */
	public preRequisiteTableDefinitions: ICommonTable;

	/**
	 * Gets or sets the PostRequisite table definitions.
	 *
	 * @type {ICommonTable}
	 * @memberof EntityActionDefinitionComponent
	 */
	public postRequisiteTableDefinitions: ICommonTable;

	/**
	 * Gets or sets the common table columns.
	 *
	 * @type {ICommonTableColumn[]}
	 * @memberof EntityActionDefinitionComponent
	 */
	public preRequisiteAvailableColumns: ICommonTableColumn[] = [];

	/**
	 * Gets or sets the common table columns.
	 *
	 * @type {ICommonTableColumn[]}
	 * @memberof EntityActionDefinitionComponent
	 */
	public preRequisiteSelectedColumns: ICommonTableColumn[] = [];

	/**
	 * Gets or sets the common table columns.
	 *
	 * @type {ICommonTableColumn[]}
	 * @memberof EntityActionDefinitionComponent
	 */
	public postRequisiteAvailableColumns: ICommonTableColumn[] = [];

	/**
	 * Gets or sets the common table columns.
	 *
	 * @type {ICommonTableColumn[]}
	 * @memberof EntityActionDefinitionComponent
	 */
	public postRequisiteSelectedColumns: ICommonTableColumn[] = [];

	/**
	 * Gets or sets the postrequisite common table context.
	 *
	 * @type {IDynamicComponentContext<CommonTableComponent, any>}
	 * @memberof EntityActionDefinitionComponent
	 */
	public postRequisiteCommonTableContext:
		IDynamicComponentContext<CommonTableComponent, any>;

	/**
	 * Gets or sets the prerequisite common table context.
	 *
	 * @type {IDynamicComponentContext<CommonTableComponent, any>}
	 * @memberof EntityActionDefinitionComponent
	 */
	public preRequisiteCommonTableContext:
		IDynamicComponentContext<CommonTableComponent, any>;

	/**
	 * Sets the action id query parameter.
	 *
	 * @type {string}
	 * @memberof EntityActionDefinitionComponent
	 */
	private readonly actionIdQueryParameter: string =
		AppConstants.commonProperties.actionId;

	/**
	 * Saves the updated entity data.
	 *
	 * @async
	 * @memberof EntityActionDefinitionComponent
	 */
	public async saveAction(): Promise<void>
	{
		const workflowActionDataObject: IWorkflowActionDefinitions =
			<IWorkflowActionDefinitions>
			{
				id: this.workflowActionDefinition.id,
				entityTypeId: this.workflowActionDefinition.entityTypeId,
				entityVersionId: this.workflowActionDefinition.entityVersionId,
				name: this.contextData.name,
				description: this.contextData.description,
				classReference:
					AnyHelper.isNullOrWhitespace(
						this.contextData.classReference)
						? null
						: this.contextData.classReference,
				classDefinition:
					AnyHelper.isNullOrWhitespace(
						this.contextData.classDefinition)
						? null
						: this.contextData.classDefinition,
				failureActionId: this.contextData.failureActionId
			};

		await this.workflowActionDefinitionsApiService
			.update(
				this.workflowActionDefinition.id,
				workflowActionDataObject);
	}

	/**
	 * Sets the context data required for this component.
	 *
	 * @async
	 * @memberof EntityActionDefinitionComponent
	 */
	public async setContextData(): Promise<void>
	{
		this.preRequisiteAvailableColumns =
			[
				{
					dataKey: 'name',
					columnHeader: 'Name',
					displayOrder: 1
				},
				{
					dataKey: 'order',
					columnHeader: 'Order',
					displayOrder: 2
				}
			];
		this.preRequisiteSelectedColumns = this.preRequisiteAvailableColumns;

		this.postRequisiteAvailableColumns =
			[
				{
					dataKey: 'name',
					columnHeader: 'Name',
					displayOrder: 1
				},
				{
					dataKey: 'order',
					columnHeader: 'Order',
					displayOrder: 2
				}
			];
		this.postRequisiteSelectedColumns = this.postRequisiteAvailableColumns;

		this.subscriptions.add(
			this.route.queryParams.subscribe((parameters: Params) =>
			{
				const mappedRouteData: any =
						ObjectHelper.mapFromRouteData(
							parameters);

				this.actionId =
						AnyHelper.isNullOrEmpty(
							mappedRouteData[this.actionIdQueryParameter])
							? AppConstants.empty
							: mappedRouteData[this.actionIdQueryParameter];
			}));

		this.workflowActionDefinition =
			await this.workflowActionDefinitionsApiService
				.get(this.actionId);

		this.entityType =
			await this.entityTypeApiService
				.get(this.workflowActionDefinition.entityTypeId);

		this.requisiteTypes =
			await this.workflowRequisiteTypesApiService
				.query(
					AppConstants.empty,
					AppConstants.empty);

		this.contextData =
			{
				id: this.workflowActionDefinition.id,
				name: this.workflowActionDefinition.name,
				description: this.workflowActionDefinition.description,
				failureActionId:
					this.workflowActionDefinition.failureActionId,
				classReference:
					this.workflowActionDefinition.classReference,
				classDefinition:
					this.workflowActionDefinition.classDefinition,
				entityTypeId:
					this.workflowActionDefinition.entityTypeId,
				entityVersionId:
					this.workflowActionDefinition.entityVersionId
			};

		this.saveTitle = 'Workflow Action Definition';
		this.saveContent = 'Workflow Action Definition'
			+ ` ${this.workflowActionDefinition.name}`;
	}

	/**
	 * Sets the Table Definitions.
	 *
	 * @async
	 * @memberof EntityActionDefinitionComponent
	 */
	public async setTableDefinitions(): Promise<void>
	{
		this.requisiteOptions =
			(await this.workflowActionDefinitionsApiService
				.query(
					'EntityTypeId eq '
						+ `${this.workflowActionDefinition.entityTypeId} `
						+ `and Id ne ${this.workflowActionDefinition.id}`,
					AppConstants.empty))
				.map(
					(actionDefinition: IWorkflowActionDefinitions) =>
						<IDropdownOption>
						{
							label: actionDefinition.name,
							value: actionDefinition.id
						});

		this.loadingPreRequisiteTableDefinitions = true;
		this.loadingPostRequisiteTableDefinitions = true;
		await this.setPreRequisiteTableDefinitions();
		await this.setPostRequisiteTableDefinitions();
	}

	/**
	 * Sets the PreRequisite Table Definitions.
	 * @async
	 * @memberof EntityActionDefinitionComponent
	 */
	public async setPreRequisiteTableDefinitions(): Promise<void>
	{
		this.preRequisiteTableDefinitions =
			{
				tableTitle: 'PreRequisites',
				expandTitle: () =>
					TableHelper.getExpandTitle(
						this.postRequisiteCommonTableContext,
						'PreRequisite'),
				hideSettings: true,
				nestedTable: true,
				objectSearch: {
					filter: 'actionDefinitionId eq '
						+ this.workflowActionDefinition.id
						+ ' and requisiteTypeId eq '
						+ (this.requisiteTypes.filter(
							(requisiteType) =>
								requisiteType.name ===
									AppConstants.workflowRequisiteTypes
										.preRequisite))[0]?.id,
					orderBy: `Order ${AppConstants.sortDirections.ascending}`,
					offset: 0,
					limit: AppConstants.dataLimits.large,
					virtualIndex: 0,
					virtualPageSize: 5
				},
				apiPromise: async (objectSearch: IObjectSearch) =>
				{
					const actionRequisites: IWorkflowActionRequisites[] =
						await this.workflowActionRequisitesApiService
							.query(
								objectSearch.filter,
								objectSearch.orderBy,
								objectSearch.offset,
								objectSearch.limit);

					const preRequisites: IWorkflowActionRequisites[] = [];

					for (const actionRequisite of actionRequisites)
					{
						preRequisites.push(
							await this.decorateActionRequisiteViewModel(
								actionRequisite));
					}

					return preRequisites;
				},
				availableColumns: this.preRequisiteAvailableColumns,
				selectedColumns: this.preRequisiteSelectedColumns,
				commonTableContext: (commonTableContext:
					IDynamicComponentContext<CommonTableComponent, any>) =>
				{
					this.preRequisiteCommonTableContext = commonTableContext;
				},
				actions: {
					create: {
						layout: [
							{
								key: 'requisiteActionDefinitionId',
								type:
									FormlyConstants.customControls.customSelect,
								wrappers: [
									FormlyConstants.customControls
										.customFieldWrapper
								],
								props: {
									label: 'Action Definition',
									required: true,
									showClear: true,
									placeholder: AppConstants.placeholders
										.selectAnOption,
									appendTo:
										FormlyConstants.appendToTargets.body,
									options: this.requisiteOptions
								}
							},
							{
								key: 'order',
								type: FormlyConstants.customControls
									.customInputNumber,
								wrappers: [
									FormlyConstants.customControls
										.customFieldWrapper
								],
								props: {
									label: 'Order',
									required: true
								},
								asyncValidators: {
									uniqueOrder: {
										expression:
											(control: UntypedFormControl) =>
												this.uniqueOrder(
													control,
													AppConstants
														.workflowRequisiteTypes
														.preRequisite),
										message: 'Existing Order.'
									}
								}
							}
						],
						items:
						[
							{
								label: 'Save',
								styleClass:
									AppConstants.cssClasses.pButtonPrimary,
								command: () => this.createRequisite(
									AppConstants.workflowRequisiteTypes
										.preRequisite)
							}
						]
					},
					updateIndex: [
						{
							id: 'updateIndexUp',
							command: async(
								selectedItem: any) =>
								this.updateOrderIndex(
									selectedItem,
									AppConstants.workflowRequisiteTypes
										.preRequisite,
									-1)
						},
						{
							id: 'updateIndexDown',
							command: async(
								selectedItem: any) =>
								this.updateOrderIndex(
									selectedItem,
									AppConstants.workflowRequisiteTypes
										.preRequisite,
									1)
						}
					],
					delete: {
						deleteStatement:
							() =>
							{
								const selectedItem: any =
									this.preRequisiteCommonTableContext.source
										.selectedItem;

								return `Confirm to remove PreRequisite
									${selectedItem.name}
									from action definition
									${this.workflowActionDefinition.name}.`;
							},
						items: [
							{
								label: 'Remove',
								styleClass:
									AppConstants.cssClasses.pButtonDanger,
								command: async() =>
									this.deleteRequisite(
										AppConstants.workflowRequisiteTypes
											.preRequisite)
							}
						],
					}
				}
			};

		this.loadingPreRequisiteTableDefinitions = false;
	}

	/**
	 * Sets the PostRequisite Table Definitions.
	 *
	 * @async
	 * @memberof EntityActionDefinitionComponent
	 */
	public async setPostRequisiteTableDefinitions(): Promise<void>
	{
		this.postRequisiteTableDefinitions =
			{
				tableTitle: 'PostRequisites',
				expandTitle: () =>
					TableHelper.getExpandTitle(
						this.postRequisiteCommonTableContext,
						'PostRequisite'),
				hideSettings: true,
				nestedTable: true,
				objectSearch: {
					filter: 'actionDefinitionId eq '
						+ this.workflowActionDefinition.id
						+ ' and requisiteTypeId eq '
						+ (this.requisiteTypes.filter(
							(requisiteType) =>
								requisiteType.name ===
									AppConstants.workflowRequisiteTypes
										.postRequisite))[0]?.id,
					orderBy: `Order ${AppConstants.sortDirections.ascending}`,
					offset: 0,
					limit: AppConstants.dataLimits.large,
					virtualIndex: 0,
					virtualPageSize: 5
				},
				apiPromise:
					async (objectSearch: IObjectSearch) =>
					{
						const actionRequisites: IWorkflowActionRequisites[] =
								await this.workflowActionRequisitesApiService
									.query(
										objectSearch.filter,
										objectSearch.orderBy,
										objectSearch.offset,
										objectSearch.limit);

						const postRequisites: IWorkflowActionRequisites[] = [];

						for (const actionRequisite of actionRequisites)
						{
							postRequisites.push(
								await this.decorateActionRequisiteViewModel(
									actionRequisite));
						}

						return postRequisites;
					},
				availableColumns: this.postRequisiteAvailableColumns,
				selectedColumns: this.postRequisiteSelectedColumns,
				commonTableContext: (commonTableContext:
					IDynamicComponentContext<CommonTableComponent, any>) =>
				{
					this.postRequisiteCommonTableContext = commonTableContext;
				},
				actions: {
					create: {
						layout: [
							{
								key: 'requisiteActionDefinitionId',
								type:
									FormlyConstants.customControls.customSelect,
								wrappers: [
									FormlyConstants.customControls
										.customFieldWrapper
								],
								props: {
									label: 'Action Definition',
									required: true,
									showClear: true,
									placeholder: AppConstants.placeholders
										.selectAnOption,
									appendTo:
										FormlyConstants.appendToTargets.body,
									options: this.requisiteOptions
								}
							},
							{
								key: 'order',
								type: FormlyConstants.customControls
									.customInputNumber,
								wrappers: [
									FormlyConstants.customControls
										.customFieldWrapper
								],
								props: {
									label: 'Order',
									required: true
								},
								asyncValidators: {
									uniqueOrder: {
										expression:
											(control: UntypedFormControl) =>
												this.uniqueOrder(
													control,
													AppConstants
														.workflowRequisiteTypes
														.postRequisite),
										message: 'Existing Order.'
									}
								}
							}
						],
						items:
						[
							{
								label: 'Save',
								styleClass:
									AppConstants.cssClasses.pButtonPrimary,
								command: () => this.createRequisite(
									AppConstants.workflowRequisiteTypes
										.postRequisite)
							}
						]
					},
					updateIndex: [
						{
							id: 'updateIndexUp',
							command: async(
								selectedItem: any) =>
								this.updateOrderIndex(
									selectedItem,
									AppConstants.workflowRequisiteTypes
										.postRequisite,
									-1)
						},
						{
							id: 'updateIndexDown',
							command: async(
								selectedItem: any) =>
								this.updateOrderIndex(
									selectedItem,
									AppConstants.workflowRequisiteTypes
										.postRequisite,
									1)
						}
					],
					delete: {
						deleteStatement:
							() =>
							{
								const selectedItem: any =
									this.postRequisiteCommonTableContext.source
										.selectedItem;

								return `Confirm to remove PostRequisite
									${selectedItem.name}
									from action definition
									${this.workflowActionDefinition.name}.`;
							},
						items: [
							{
								label: 'Remove',
								styleClass:
									AppConstants.cssClasses.pButtonDanger,
								command: async() =>
									this.deleteRequisite(
										AppConstants.workflowRequisiteTypes
											.postRequisite)
							}
						],
					}
				}
			};

		this.loadingPostRequisiteTableDefinitions = false;
	}

	/**
	 * Decorates the view model for the object.
	 *
	 * @async
	 * @param {IWorkflowActionRequisites} actionRequisite
	 * The object to be decorated as a view model.
	 * @returns {Promise<any>}
	 * The mapped view model for display in this component.
	 * @memberof EntityActionDefinitionComponent
	 */
	public async decorateActionRequisiteViewModel(
		actionRequisite: IWorkflowActionRequisites): Promise<any>
	{
		return <any>{
			id: actionRequisite.id,
			name:
				(await this.workflowActionDefinitionsApiService
					.get(actionRequisite
						.requisiteActionDefinitionId))
					?.name,
			requisiteTypeId:
				actionRequisite.requisiteTypeId,
			actionDefinitionId:
				actionRequisite.actionDefinitionId,
			requisiteActionDefinitionId:
				actionRequisite
					.requisiteActionDefinitionId,
			order: actionRequisite.order
		};
	}

	/**
	 * Sets the Formly Definitions.
	 *
	 * @async
	 * @memberof EntityManagerDirective
	 */
	public async setFormlyDefinitions(): Promise<void>
	{
		await this.setContextData();
		this.updateLastSavedData();
		await this.setLayoutFields();

		this.loadingDefinitions = false;
	}

	/**
	 * Sets the formly layout fields.
	 *
	 * @async
	 * @memberof EntityActionDefinitionComponent
	 */
	public async setLayoutFields(): Promise<void>
	{
		this.layoutFields =
			<FormlyFieldConfig[]>
			[
				{
					key: 'name',
					type: FormlyConstants.customControls.input,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Name',
						required: true
					},
					asyncValidators: {
						uniqueCombination: {
							expression: async (control: UntypedFormControl) =>
								this.uniqueCombination(control),
							message:
								'Existing Type, Version and Name combination.'
						}
					}
				},
				{
					key: 'description',
					type: FormlyConstants.customControls.input,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Description'
					}
				},
				{
					key: 'failureActionId',
					type: FormlyConstants.customControls.customSelect,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						showClear: true,
						placeholder: AppConstants.placeholders.selectAnOption,
						label: 'Failure Action',
						required: true,
						appendTo: FormlyConstants.appendToTargets.body,
						options: (await this.workflowFailureActionsApiService
							.query(
								AppConstants.empty,
								AppConstants.empty))
							?.map(
								(failureAction: IWorkflowFailureActions) =>
									this.mapWorkflowFailureAction(
										failureAction))
					}
				},
				{
					key: 'classReference',
					type: FormlyConstants.customControls.input,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Class Reference',
						required: true
					},
					validators: {
						oneOf: {
							expression: (
								control: UntypedFormControl,
								field: FormlyFieldConfig) =>
								AnyHelper.isNullOrWhitespace(
									control.value)
									|| AnyHelper.isNullOrWhitespace(
										field.model.classDefinition),
							message:
								'Only one of class definition or class '
									+ 'reference can be set.'
						}
					},
					expressions: {
						'props.required':
							'model.classDefinition == null '
								+ '|| model.classDefinition.trim() === ""'
					}
				},
				{
					key: 'classDefinition',
					type: FormlyConstants.customControls.customTextArea,
					wrappers: [
						FormlyConstants.customControls.customFieldWrapper
					],
					props: {
						label: 'Class Definition',
						rows: FormlyConstants.textAreaRowSizes.standard,
						required: true
					},
					validators: {
						validCSharpFormat: {
							expression: (
								control: UntypedFormControl) =>
								AnyHelper.isNullOrWhitespace(control.value)
									|| this.isValidCSharpFormat(
										control.value),
							message: 'Not a valid C# format.'
						},
						oneOf: {
							expression: (
								control: UntypedFormControl,
								field: FormlyFieldConfig) =>
								AnyHelper.isNullOrWhitespace(
									control.value)
									|| AnyHelper.isNullOrWhitespace(
										field.model.classReference),
							message:
								'Only one of class definition or class '
									+ 'reference can be set.'
						}
					},
					expressions: {
						'props.required':
							'model.classReference == null '
								+ '|| model.classReference.trim() === ""'
					}
				}
			];
	}

	/**
	 * Gets the requisite table context based on the sent requisite type.
	 *
	 * @param {string} requisiteType
	 * The requisite type to get a table context for.
	 * @returns {IDynamicComponentContext<CommonTableComponent, any>}
	 * The requisite table context.
	 * @memberof EntityActionDefinitionExpandComponent
	 */
	private getRequisiteTableContext(
		requisiteType: string):
		IDynamicComponentContext<CommonTableComponent, any>
	{
		return requisiteType ===
			AppConstants.workflowRequisiteTypes.preRequisite
			? this.preRequisiteCommonTableContext
			: this.postRequisiteCommonTableContext;
	}

	/**
	 * Checks if entity type, entity verion
	 * and action name combination is existing or unique.
	 *
	 * @async
	 * @param {FormControl} control
	 * The form control.
	 * @returns {Promise<boolean>}
	 * The field async validation result.
	 * @memberof EntityActionDefinitionComponent
	 */
	private async uniqueCombination(control: UntypedFormControl):
		Promise<boolean>
	{
		let actionDefinitions: IWorkflowActionDefinitions[] = [];

		if (!AnyHelper.isNullOrEmpty(this.contextData.entityTypeId))
		{
			actionDefinitions =
				await this.workflowActionDefinitionsApiService
					.query(
						`EntityTypeId eq ${this.contextData.entityTypeId} `
							+ 'and EntityVersionId eq '
							+ `${this.contextData.entityVersionId} `
							+ `and Name eq '${control.value}' `
							+ `and Id neq ${this.contextData.id}`,
						AppConstants.empty);
		}

		return Promise.resolve(actionDefinitions.length === 0);
	}

	/**
	 * Checks if content value is a valid c sharp format.
	 *
	 * @returns {boolean | RegExpMatchArray}
	 * The validation result.
	 * @memberof EntityActionDefinitionComponent
	 */
	private isValidCSharpFormat(value: string): boolean | RegExpMatchArray
	{
		return AnyHelper.isNullOrWhitespace(value)
			|| value?.match(new RegExp(
				'^(?=.*namespace (?=WaterStreet))'
					+ '(?=.*using (?=WaterStreet))'
					+ '(?=.*public (?=class))'
					+ '(?=.*public (?=override))'
					+ '(?=.*return).*$',
				'gs'))
			&& value.match(/\{(?:[^{}]+|\{(?:[^{}]+|\{[^{}]\})\}+)\}+/g);
	}

	/**
	 * Validates if the requisite order is unique.
	 *
	 * @async
	 * @param {FormControl} control
	 * The field form control.
	 * @param {string} requisiteType
	 * The requisite type.
	 * @returns {Promise<boolean>}
	 * The field async validation result.
	 * @memberof EntityActionDefinitionComponent
	 */
	private async uniqueOrder(
		control: UntypedFormControl,
		requisiteType: string): Promise<boolean>
	{
		const workflowRequisiteTypes: IWorkflowRequisiteTypes[] =
			await this.workflowRequisiteTypesApiService
				.query(
					`Name eq '${requisiteType}'`,
					AppConstants.empty);

		const workflowRequisites: IWorkflowActionRequisites[] =
			await this.workflowActionRequisitesApiService
				.query(
					`Order eq ${control.value} `
						+ 'and ActionDefinitionId eq '
						+ `${this.workflowActionDefinition.id} `
						+ `and RequisiteTypeId eq ${
							workflowRequisiteTypes[0].id}`,
					AppConstants.empty);

		return Promise.resolve(workflowRequisites.length <= 0);
	}

	/**
	 * Creates a new Requisite.
	 *
	 * @async
	 * @param {string} requisiteType
	 * The requisite type to create.
	 * @memberof EntityActionDefinitionExpandComponent
	 */
	private async createRequisite(
		requisiteType: string): Promise<void>
	{
		const commonTableContext:
			IDynamicComponentContext<CommonTableComponent, any> =
			this.getRequisiteTableContext(
				requisiteType);

		const createRequisite: Function =
			async () =>
			{
				const requisiteTypeId: number =
					(await this
						.workflowRequisiteTypesApiService
						.query(
							`Name eq '${requisiteType}'`,
							AppConstants.empty))[0].id;

				const createdId: number =
					await this.workflowActionRequisitesApiService
						.create(
							<IWorkflowActionRequisites>
							{
								id: 0,
								requisiteTypeId: requisiteTypeId,
								actionDefinitionId:
									this.workflowActionDefinition.id,
								requisiteActionDefinitionId:
									commonTableContext.source.selectedItem
										.requisiteActionDefinitionId,
								order:
									commonTableContext.source.selectedItem
										.order
							});

				commonTableContext.source.selectedItem.id =
					createdId;
				commonTableContext.source.selectedItem.requisiteTypeId =
					requisiteTypeId;
				commonTableContext.source.selectedItem.actionDefinitionId =
					this.workflowActionDefinition.id;

				await commonTableContext.source.addSelectedItem();
				this.sortCommonTableByOrder(commonTableContext);
			};

		await this.activityService.handleActivity(
			new Activity(
				createRequisite(),
				'<strong>Creating Requisite</strong>',
				'<strong>Created Requisite</strong>',
				`Requisite
					${commonTableContext.source.selectedItem.name}
					was successfully created.`,
				`Requisite
					${commonTableContext.source.selectedItem.name}
					was not created.`));
	}

	/**
	 * Deletes an existing Requisite.
	 *
	 * @async
	 * @param {string} requisiteType
	 * The requisite type to delete.
	 * @memberof EntityActionDefinitionComponent
	 */
	private async deleteRequisite(
		requisiteType: string): Promise<void>
	{
		const commonTableContext:
			IDynamicComponentContext<CommonTableComponent, any> =
			this.getRequisiteTableContext(
				requisiteType);

		const selectedItem: any =
			commonTableContext.source.selectedItem;

		const deleteRequisite: Function =
			async() =>
			{
				await this.workflowActionRequisitesApiService
					.delete(selectedItem.id);

				commonTableContext.source.deleteSelectedItem();
			};

		await this.activityService.handleActivity(
			new Activity(
				deleteRequisite(),
				'<strong>Deleting Requisite</strong>',
				'<strong>Deleted Requisite</strong>',
				`Requisite ${selectedItem.name}
					was successfully deleted.`,
				`Requisite ${selectedItem.name}
					was not deleted.`));
	}

	/**
	 * Updates the order index of the selected row item
	 * up or down based on the indexOperator.
	 *
	 * @async
	 * @param {any} selectedItem
	 * The selected item.
	 * @param {string} requisiteType
	 * The requisite type to alter orders for.
	 * @param {number} indexReference
	 * The index reference to add or substract to the current
	 * selected order index.
	 * @memberof EntityActionDefinitionComponent
	 */
	private async updateOrderIndex(
		selectedItem: any,
		requisiteType: string,
		indexReference: number): Promise<void>
	{
		const commonTableContext:
			IDynamicComponentContext<CommonTableComponent, any> =
			this.getRequisiteTableContext(
				requisiteType);
		commonTableContext.source.loadingNextDataset = true;
		commonTableContext.source.selectedItem = selectedItem;

		const updateOrderIndex: Function =
			async() =>
			{
				const orderedRequisites: any[] =
					commonTableContext.source.virtualData
						.filter((data) => data !== undefined)
						.sort((itemOne: any, itemTwo: any) =>
							itemOne.order - itemTwo.order);

				const neighborOrderIndex: number =
					this.findSelectedChildIndex(
						orderedRequisites,
						selectedItem)
						+ indexReference;

				await this.workflowActionRequisitesApiService
					.update(
						orderedRequisites[neighborOrderIndex].id,
						<IWorkflowActionRequisites>
						{
							id: orderedRequisites[
								neighborOrderIndex].id,
							requisiteTypeId: orderedRequisites[
								neighborOrderIndex]
								.requisiteTypeId,
							actionDefinitionId:
								orderedRequisites[neighborOrderIndex]
									.actionDefinitionId,
							requisiteActionDefinitionId:
								orderedRequisites[neighborOrderIndex]
									.requisiteActionDefinitionId,
							order: 1000
						});

				await this.workflowActionRequisitesApiService
					.update(
						selectedItem.id,
						<IWorkflowActionRequisites>
						{
							id: selectedItem.id,
							requisiteTypeId:
								selectedItem
									.requisiteTypeId,
							actionDefinitionId:
								selectedItem
									.actionDefinitionId,
							requisiteActionDefinitionId:
								selectedItem
									.requisiteActionDefinitionId,
							order: orderedRequisites[
								neighborOrderIndex].order
						});

				await this.workflowActionRequisitesApiService
					.update(
						orderedRequisites[neighborOrderIndex].id,
						<IWorkflowActionRequisites>
						{
							id: orderedRequisites[
								neighborOrderIndex].id,
							requisiteTypeId:
								orderedRequisites[neighborOrderIndex]
									.requisiteTypeId,
							actionDefinitionId:
								orderedRequisites[neighborOrderIndex]
									.actionDefinitionId,
							requisiteActionDefinitionId:
								orderedRequisites[neighborOrderIndex]
									.requisiteActionDefinitionId,
							order:
								selectedItem.order
						});

				commonTableContext.source.virtualData[neighborOrderIndex] =
					{
						...orderedRequisites[neighborOrderIndex],
						order: selectedItem.order
					};
				commonTableContext.source.virtualData[
					neighborOrderIndex - indexReference] =
					{
						...commonTableContext.source.selectedItem,
						order: orderedRequisites[neighborOrderIndex].order
					};

				this.sortCommonTableByOrder(commonTableContext);
				commonTableContext.source.loadingNextDataset = false;
			};

		await this.activityService.handleActivity(
			new Activity(
				updateOrderIndex(),
				'<strong>Updating Requisite Order</strong>',
				'<strong>Updated Requisite Order</strong>',
				'Requisite Order was successfully updated.',
				'Requisite Order was not updated.'),
			AppConstants.activityStatus.complete,
			true);
	}

	/**
	 * Sorts the common table context data by order.
	 *
	 * @param {IDynamicComponentContext<CommonTableComponent, any>}
	 * commonTableContext
	 * The common table context to sort by order.
	 * @memberof EntityActionDefinitionComponent
	 */
	private sortCommonTableByOrder(
		commonTableContext:
			IDynamicComponentContext<CommonTableComponent, any>): void
	{
		commonTableContext.source.virtualData
			.sort((itemOne: any, itemTwo: any) =>
				itemOne.order - itemTwo.order);
		commonTableContext.source.virtualData =
			[
				...commonTableContext.source.virtualData
			];
	}

	/**
	 * Finds the selected child index.
	 *
	 * @param {any[]} children
	 * The children object array.
	 * @param {any} selectedChild
	 * The selected child.
	 * @returns {number}
	 * The child index selected.
	 * @memberof EntityActionDefinitionComponent
	 */
	private findSelectedChildIndex(
		children: any[],
		selectedChild: any): number
	{
		for (let index = 0; index < children.length; index++)
		{
			if (children[index].id === selectedChild.id
				&& children[index].requisiteActionDefinitionId
					=== selectedChild.requisiteActionDefinitionId
				&& children[index].order === selectedChild.order)
			{
				return index;
			}
		}

		return -1;
	}

	/**
	 * Maps and returns a workflow failure action as a dropdown option.
	 *
	 * @param {IWorkflowFailureActions} failureAction
	 * The workflow failure action to be mapped.
	 * @returns {IDropdownOption}
	 * A mapped dropdown option representing the workflow failure action.
	 * @memberof EntityActionDefinitionComponent
	 */
	private mapWorkflowFailureAction(
		failureAction: IWorkflowFailureActions): IDropdownOption
	{
		return <IDropdownOption>
			{
				label: failureAction.name,
				value: failureAction.id
			};
	}
}